import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  LeaveRequestListItem,
  LeaveRequestStatus,
} from "@/modules/hr/leave/domain/types";
import { useLeaveRequestsQuery } from "../api/queries";
import {
  LeaveHistoryTour,
  useLeaveHistoryTour,
} from "../tour/LeaveHistoryTour";
import {
  LeaveApprovalFilter,
  type LeaveApprovalSearchParams,
} from "../ui/LeaveApprovalFilter";
import { LeaveApprovalListTable } from "../ui/LeaveApprovalListTable";

export function LeaveHistoryPage() {
  const nav = useNavigate({ from: "/app/hr/leave/history" });
  const search = useSearch({
    from: "/app/hr/leave/history",
  }) as LeaveApprovalSearchParams & {
    offset?: number;
    limit?: number;
    periodFromOnly?: boolean;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const sortBy = search.sort?.[0]?.field ?? "startDate";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const query = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      periodYear: search.periodYear ?? currentYear,
      periodMonth: search.periodMonth ?? currentMonth,
      status:
        search.status && search.status !== "__all__"
          ? (search.status as LeaveRequestStatus)
          : undefined,
      search: search.search ?? undefined,
    }),
    [
      search.offset,
      search.limit,
      search.sort,
      search.periodYear,
      search.periodMonth,
      search.status,
      search.search,
      currentYear,
      currentMonth,
    ],
  );

  const listQuery = useLeaveRequestsQuery(query);
  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        periodYear: search.periodYear ?? currentYear,
        periodMonth: search.periodMonth ?? currentMonth,
        periodFromOnly: search.periodFromOnly ?? false,
        status: search.status,
        search: search.search,
      },
    });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          periodYear: search.periodYear ?? currentYear,
          periodMonth: search.periodMonth ?? currentMonth,
          periodFromOnly: search.periodFromOnly ?? false,
          status: search.status,
          search: search.search,
        },
      });
    },
    [
      nav,
      search.limit,
      search.periodYear,
      search.periodMonth,
      search.periodFromOnly,
      search.status,
      search.search,
    ],
  );

  const searchForDetail = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      periodYear: search.periodYear ?? currentYear,
      periodMonth: search.periodMonth ?? currentMonth,
      periodFromOnly: search.periodFromOnly ?? false,
      status: search.status,
      search: search.search,
      from: "history" as const,
    }),
    [
      search.offset,
      search.limit,
      search.sort,
      search.periodYear,
      search.periodMonth,
      search.periodFromOnly,
      search.status,
      search.search,
      currentYear,
      currentMonth,
    ],
  );
  const handleViewDetail = useCallback(
    (row: LeaveRequestListItem) => {
      nav({
        to: "/app/hr/leave/requests/$id",
        params: { id: row.id },
        search: searchForDetail,
      });
    },
    [nav, searchForDetail],
  );

  const tour = useLeaveHistoryTour();

  return (
    <>
      <Header />
      <Main>
        <LeaveHistoryTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center gap-2"
          data-tourid="leave-history-toolbar"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ປະຫວັດການລາ / ສະຫຼຸບ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">
              ເບິ່ງປະຫວັດການລາຕາມພະນັກງານ ເດືອນ ປະເພດລາ ເພື່ອ audit ແລະ ລາຍງານ
            </p>
          </div>
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="leave-history-filter"
        >
          <LeaveApprovalFilter
            searchParams={{
              ...search,
              periodYear: search.periodYear ?? currentYear,
              periodMonth: search.periodMonth ?? currentMonth,
              status: search.status,
            }}
            fromRoute="/app/hr/leave/history"
            defaultStatus="__all__"
          />
          <div data-tourid="leave-history-table">
            <LeaveApprovalListTable
              data={data}
              isLoading={listQuery.isLoading}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              onPaginationChange={handlePaginationChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortingChange={handleSortingChange}
              onViewDetail={handleViewDetail}
              onApprove={() => {}}
              onReject={() => {}}
              showActions={false}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
